<template>
  <div v-if="itemClass === 'Transfers'">
    <el-popover
      placement="top"
      trigger="hover"
      width="230"
      v-if="
        shouldShowTransferTooltip(
          reservation.stage,
          reservationStatus,
          reservation.created_at,
          reservation.status_update,
          reservation.pickup_date,
          reservation.branch.timezone
        )
      "
    >
      <span class="text-center d-block">{{
        getTransferTooltipMessage(
          reservationStatus,
          reservation.created_at,
          reservation.status_update,
          reservation.pickup_date,
          reservation.branch.timezone,
          reservation.utcExpiresAt
        )
      }}</span>

      <div
        slot="reference"
        class="badge-status"
        v-bind:class="
          getTransferStatusClass(
            reservation.stage,
            reservationStatus,
            reservation.created_at,
            reservation.status_update,
            reservation.pickup_date,
            reservation.branch.timezone
          )
        "
      >
        {{
          getTransferStatusLabel(
            reservation.stage,
            reservationStatus,
            reservation.status_update
          )
        }}
      </div>
    </el-popover>

    <div
      v-else
      class="badge-status"
      v-bind:class="
        getReservationStatusClass(reservation.stage, reservationStatus)
      "
    >
      {{ getReservationStatusLabel(reservation.stage, reservationStatus) }}
    </div>
  </div>
  <div v-else>
    <div
      class="badge-status"
      v-bind:class="
        getReservationStatusClass(reservation.stage, reservationStatus)
      "
    >
      {{ getReservationStatusLabel(reservation.stage, reservationStatus) }}
    </div>
    <el-popover
      placement="top"
      trigger="hover"
      width="80"
      class="curp"
      v-if="reservationStatus === 'ON_HOLD'"
    >
      <span class="text-center d-block w-100"> Expiration time </span>
      <div slot="reference">
        <div class="d-flex justify-content-center align-items-center mt-1">
          <Countdown :date="reservation.utcExpiresAt" type="sm" />
        </div>
      </div>
    </el-popover>
  </div>
</template>

<script>
import moment from "moment-timezone";
import Countdown from "@/components/Countdown.vue";

export default {
  name: "ReservationStatusBadge",
  components: {
    Countdown
  },
  props: {
    reservation: {
      type: Object,
      required: true
    },
    itemClass: {
      type: String,
      default: ""
    }
  },
  methods: {
    // ---- Quote status (transfer still in QUOTE stage) ----
    quoteLabel(status) {
      if (status === "NEW") {
        return "Processing";
      } else if (status === "DRAFT") {
        return "Draft";
      } else if (status === "CANCELLED") {
        return "Cancelled";
      } else if (status === "EXPIRED") {
        return "Expired";
      } else if (status === "REJECTED") {
        return "Rejected";
      }
      return "Unknown Status";
    },
    quoteCssClass(status) {
      if (status === "NEW") {
        return "status-processing";
      } else if (status === "CANCELLED") {
        return "status-cancelled";
      } else if (status === "DRAFT") {
        return "status-draft";
      } else if (status === "EXPIRED") {
        return "status-expired";
      } else if (status === "REJECTED") {
        return "status-rejected";
      }
      return {};
    },

    // ---- Transfer reservation status (badge label/class/tooltip) ----
    // Each method only resolves the QUOTE-stage shortcut and the "dynamic"
    // statuses (those depending on cutOff/diff); everything else
    // is delegated to a smaller, status-only helper.
    getTransferStatusLabel(stage, status, statusUpdate) {
      if (stage === "QUOTE") {
        return this.quoteLabel(status);
      }

      if (status === "SC") {
        return statusUpdate
          ? "Reconfirm"
          : this.getReservationStatusLabel(stage, status);
      }

      return this.getReservationStatusLabel(stage, status);
    },
    getTransferStatusClass(
      stage,
      status,
      createdAt,
      statusUpdate,
      pickupDate,
      timeZone
    ) {
      if (stage === "QUOTE") {
        return this.quoteCssClass(status);
      }
      let cutOff = this.isPickupWithinNext72Hours(pickupDate, timeZone);
      if (status === "NW") {
        let diff = this.isCreatedOverdue(createdAt);
        return this.getNewStatusBadgeClass(cutOff, diff, statusUpdate);
      } else if (status === "SC") {
        return this.getConfirmedStatusBadgeClass(cutOff, statusUpdate);
      } else if (status === "SRB") {
        return cutOff ? "status-rejected critical-rejected" : "status-rejected";
      }
      return this.getStaticTransferStatusClass(status);
    },
    getStaticTransferStatusClass(status) {
      if (["SCB", "SBC"].includes(status)) {
        return "status-cancelled";
      } else if (["DA", "DSR", "DAP", "POB"].includes(status)) {
        return "status-started";
      } else if (status === "JF") {
        return "status-completed";
      } else if (status === "NS") {
        return "status-noshow";
      } else if (status === "BI") {
        return "status-draft";
      } else if (status === "EXPIRED") {
        return "status-expired";
      } else if (status === "PNDG") {
        return "status-draft";
      }
      return {};
    },
    shouldShowTransferTooltip(
      stage,
      currentStatus,
      createdAt,
      statusUpdate,
      pickupDate,
      timeZone
    ) {
      if (stage === "QUOTE") return false;

      let cutOff = this.isPickupWithinNext72Hours(pickupDate, timeZone);
      if (currentStatus === "NW") {
        let diff = this.isCreatedOverdue(createdAt);
        return this.shouldShowNewStatusTooltip(cutOff, diff, statusUpdate);
      } else if (currentStatus === "SC") {
        return statusUpdate;
      } else if (currentStatus === "PNDG") {
        return true;
      } else if (currentStatus === "SRB") {
        return cutOff ? true : false;
      }
      return false;
    },
    shouldShowNewStatusTooltip(cutOff, diff, statusUpdate) {
      if (cutOff) {
        return true;
      }
      return diff ? true : statusUpdate ? true : false;
    },
    getTransferTooltipMessage(
      currentStatus,
      createdAt,
      statusUpdate,
      pickupDate,
      timeZone,
      utcExpiresAt
    ) {
      let cutOff = this.isPickupWithinNext72Hours(pickupDate, timeZone);
      if (currentStatus === "NW") {
        let diff = this.isCreatedOverdue(createdAt);
        return this.getNewStatusTooltipMessage(
          cutOff,
          diff,
          statusUpdate,
          createdAt,
          pickupDate,
          timeZone
        );
      } else if (currentStatus === "SC") {
        return this.getConfirmedStatusTooltipMessage(
          statusUpdate,
          cutOff,
          pickupDate,
          timeZone
        );
      } else if (currentStatus === "PNDG") {
        return `Payment is required from the partner before the supplier can confirm. There are ${this.getTimeUntilLabel(
          this.getPaymentWindowDeadline(utcExpiresAt, timeZone),
          timeZone
        )} left before the payment window closes.`;
      } else if (currentStatus === "EXPIRED") {
        return "The quote period for this reservation has expired. You can resume the reservation once it has been re-quoted.";
      } else if (currentStatus === "SRB") {
        return this.getRejectedTooltipMessage(cutOff, pickupDate, timeZone);
      } else {
        return "";
      }
    },
    getPaymentWindowDeadline(utcExpiresAt, timeZone) {
      return moment(utcExpiresAt)
        .add(72, "hours")
        .tz(timeZone)
        .utc(true);
    },
    getRejectedTooltipMessage(cutOff, pickupDate, timeZone) {
      return cutOff
        ? ` This reservation is ${this.getTimeUntilLabel(
            pickupDate,
            timeZone
          )} away from the pickup date and has not been confirmed.`
        : "";
    },

    // ---- Generic reservation status (used outside the QUOTE/Transfer tooltip flow) ----
    // Only resolves the QUOTE-stage shortcut; the plain status -> text/class
    // mapping is delegated to mapReservationStatusToLabel/Class.
    getReservationStatusLabel(stage, status) {
      if (stage === "QUOTE") {
        return this.quoteLabel(status);
      }
      return this.mapReservationStatusToLabel(status);
    },
    mapReservationStatusToLabel(status) {
      if (status === "NW") {
        return "New";
      } else if (["CANCELLED", "SBC", "SCB"].includes(status)) {
        return "Cancelled";
      } else if (["CONFIRMED", "SC"].includes(status)) {
        return "Confirmed";
      } else if (status === "JF") {
        return "Completed";
      } else if (status === "SRB") {
        return "Rejected";
      } else if (status === "DA") {
        return "Driver Accepted";
      } else if (status === "DSR") {
        return "Driver Started Trip";
      } else if (status === "DAP") {
        return "Driver on Location";
      } else if (status === "POB") {
        return "POB";
      } else if (status === "NS") {
        return "No Show";
      } else if (["ON_HOLD", "PNDG"].includes(status)) {
        return "Pending Payment";
      } else if (["EXPIRED", "EXP"].includes(status)) {
        return "Expired";
      } else if (status === "RCF") {
        return "Reconfirm";
      } else {
        return "Draft";
      }
    },
    getReservationStatusClass(stage, status) {
      if (stage === "QUOTE") {
        return this.quoteCssClass(status);
      }
      return this.mapReservationStatusToClass(status);
    },
    mapReservationStatusToClass(status) {
      if (status === "NW") {
        return "status-new";
      } else if (["CONFIRMED", "SC"].includes(status)) {
        return "status-confirmed";
      } else if (["SCB", "SBC", "CANCELLED"].includes(status)) {
        return "status-cancelled";
      } else if (status === "SRB") {
        return "status-rejected";
      } else if (["DA", "DSR", "DAP", "POB"].includes(status)) {
        return "status-started";
      } else if (status === "JF") {
        return "status-completed";
      } else if (status === "NS") {
        return "status-noshow";
      } else if (status === "BI") {
        return "status-draft";
      } else if (["PNDG", "ON_HOLD"].includes(status)) {
        return "status-draft";
      } else if (["EXPIRED"].includes(status)) {
        return "status-expired";
      } else {
        return {};
      }
    },

    // ---- Badge class/message variants for the "NW" and "SC" statuses ----
    getNewStatusBadgeClass(cutOff, diff, statusUpdate) {
      if (cutOff) {
        if (diff) {
          return "status-overdue critical-overdue";
        } else {
          return statusUpdate
            ? "status-reconfirm critical-reconfirmed"
            : "status-new critical-new";
        }
      } else {
        if (diff) {
          return "status-overdue";
        } else {
          return statusUpdate ? "status-reconfirm" : "status-new";
        }
      }
    },
    getConfirmedStatusBadgeClass(cutOff, statusUpdate) {
      if (statusUpdate) {
        return cutOff
          ? "status-reconfirm critical-reconfirmed"
          : "status-reconfirm";
      } else {
        return "status-confirmed";
      }
    },
    getNewStatusTooltipMessage(
      cutOff,
      diff,
      statusUpdate,
      createdAt,
      pickupDate,
      timeZone
    ) {
      if (cutOff) {
        if (diff) {
          return `This reservation was created ${this.getElapsedTimeLabel(
            createdAt
          )} ago and is now ${this.getTimeUntilLabel(
            pickupDate,
            timeZone
          )} away from the pickup date without confirmation.`;
        } else {
          return statusUpdate
            ? `This updated reservation is  ${this.getTimeUntilLabel(
                pickupDate,
                timeZone
              )} away from the pickup date and still needs to be confirmed.`
            : `This reservation is ${this.getTimeUntilLabel(
                pickupDate,
                timeZone
              )} way from the pickup date and still needs to be confirmed.`;
        }
      } else {
        if (diff) {
          return `This reservation has not been confirmed, and it has been ${this.getElapsedTimeLabel(
            createdAt
          )} since it was created.`;
        } else {
          return statusUpdate
            ? "This reservation has been updated but is not yet confirmed."
            : "";
        }
      }
    },
    getConfirmedStatusTooltipMessage(
      statusUpdate,
      cutOff,
      pickupDate,
      timeZone
    ) {
      if (statusUpdate) {
        return cutOff
          ? `This updated reservation is ${this.getTimeUntilLabel(
              pickupDate,
              timeZone
            )} away from the pickup date and still needs to be reconfirmed.`
          : "This reservation has been updated and needs to be reconfirmed.";
      } else {
        return "";
      }
    },

    // ---- Date/time helpers ----
    getHoursSince(date) {
      return moment(moment().format()).diff(
        moment(date).format(),
        "hours",
        true
      );
    },
    isCreatedOverdue(createdAt) {
      return this.getHoursSince(createdAt) >= 48;
    },
    getElapsedTimeLabel(createdAt) {
      let hours = this.getHoursSince(createdAt);
      return this.formatHoursAsLabel(hours);
    },
    getTimeUntilLabel(targetDate, timeZone) {
      let currentDate = moment.tz(timeZone).utc(true);
      let duration = moment.duration(moment(targetDate).diff(currentDate));
      let hours = Math.floor(duration.asHours());
      return this.formatHoursAsLabel(hours);
    },
    formatHoursAsLabel(hours) {
      const totalMinutes = Math.floor(hours * 60);
      const days = Math.floor(totalMinutes / (60 * 24));
      const hoursRemaining = Math.floor((totalMinutes % (60 * 24)) / 60);

      return this.formatDaysHoursLabel(days, hoursRemaining);
    },
    isPickupWithinNext72Hours(pickupDate, timeZone) {
      let currentDate = moment()
        .tz(timeZone)
        .startOf("day");
      let thresholdDate = moment()
        .tz(timeZone)
        .add(3, "days")
        .endOf("days");
      return moment(pickupDate).isBetween(currentDate, thresholdDate);
    },
    formatDaysHoursLabel(days, hours) {
      let result = "";
      if (days === 1) {
        result += `${days} day `;
      } else if (days > 1) {
        result += `${days} days `;
      }
      if (hours === 1) {
        result += `${hours} hour `;
      } else if (hours > 1) {
        result += `${hours} hours `;
      }
      return result.trim();
    }
  },
  computed: {
    reservationStatus() {
      return this.reservation.status;
    }
  }
};
</script>
